<template>
  <div>
    <main v-if="campaign">
      <h1 class="page-header">new campaign</h1>

      <agreement-modal v-if="shouldShowAgreementModal"
                       @rejectAgreementModal="rejectAgreementModal" />

      <modal
        v-if="showAdChangedModal"
        wrapperClasses="modal"
        @close="showAdChangedModal = false">
        <h3 slot="header" class="title">sponsored image update</h3>
        <div slot="body" class="body">
          <p>a change has been made to the sponsored image during your campaign creation, review the sponsored image and reattach the image to your campaign to send free credits.</p>
        </div>
        <div
          slot="action"
          class="button larger modal-click-action"
          @click="showAdChangedModal = false">
          continue
        </div>
      </modal>

      <modal
        v-if="emailExtensionWarning.showModal"
        wrapperClasses="modal email-extension-warning-modal"
        :show-close="true"
        @close="emailExtensionWarning.showModal = false">
        <h3 slot="header" class="title" v-html="emailExtensionWarning.title"/>
        <div slot="body" class="body">
          <p>{{emailExtensionWarning.text}}</p>
        </div>
        <div slot="action" class="button-wrapper">
          <button
            class="button larger modal-click-action"
            @click="emailExtensionWarning.showModal = false">
            edit campaign
          </button>
          <a
            class="proceed-link"
            @click="emailExtensionWarning.ignore = true; sendCampaign()">
            send campaign anyway
          </a>
        </div>
      </modal>

      <div class="banner" v-if="selectedAd">
        your campaign is now completely free because one of your brands images has been attached - great work!
      </div>

      <div class="banner emoji-alert" v-if="smsContentHasEmoji">
        <div class="warning-message">
          {{ this.newEmojiCalculationTypeEnabled ? this.emojiAlertMessage : this.classicEmojiAlertMessage }}
        </div>
      </div>

      <section class="content">
        <div class='left-side'>
          <error-banner />

          <recipient-dropdown
            :pre-selected-filters="adFilters"
            :groups-with-selected="groupsWithSelected"
            :campaign-id="campaign.id"
            @update:selected="updateSelectedRecipients"
            @update:tagSubfilter="updateTagSubfilter"
            style="margin-bottom:35px"
            data-cy="recipient-filter-button"
            ref="recipientDropdown"
            @updatedSelectedGroups="updateSelectedGroups"
          >
            <div>recipient filters</div>
            <div>(<counter :start="prevRecipientCount" :end="totalRecipientCount" />)</div>
          </recipient-dropdown>

          <tab-bar
            class='campaign-tabs'
            :tabs="tabs"
            :selected.sync="selectedTab"
            @update:selected="updateSelectedTab"
            data-cy="campaign-tab"
          />

          <div v-if="selectedTab && selectedTab.key === 'stashboard'">
            <div class='campaign-content-toggle text-can-error' @click="smsExpanded = !smsExpanded" v-has-error:sms_send>
              <span v-if="smsExpanded">-</span>
              <span v-else>+</span>
              <span>text message</span>
            </div>

            <div v-if="smsExpanded">
              <campaign-creator
                :ownerType="'Campaign'"
                :campaign="campaign"
                :ad="selectedAd"
                :editorValue="editorValue"
                :stashboard-headers="stashboardHeaders"
                @update:editor-value="updateEditorAndStashboardPreviewContentValue"
                @update:ad="updateAd"
                style="margin-bottom:20px;"
                @update:campaign="updateCampaign"
              />

              <div class="stashboard-preview-toggle" @click="stashboardPreviewExpanded = !stashboardPreviewExpanded">
                <span v-if="stashboardPreviewExpanded">- hide preview</span>
                <span v-if="!stashboardPreviewExpanded">+ see preview</span>
              </div>

              <div v-if="stashboardPreviewExpanded" class="pl-3.5 mb-10">
                <stashboard-preview :stashboard="stashboardPreview" :previewExtension="previewExtension"/>
              </div>

              <text-notification-picker
                v-if="!classicCampaignsEnabled"
                class="notification-picker"
                :selected.sync="selectedNotificationTemplate"
                :templates="templates"
                @update:selected="updatedSelectedNotification"
                @deleteTemplate="(templateId) => templates = [ ...templates.filter(t => t.id !== templateId)]"
                v-touch:template_id
                v-has-error:template_id
                style="margin-bottom:15px"
                :campaignId="campaignId"
                :isLoading="isLoadingNotifTemplates"
                @triggerUpdate="fetchNotifications"
              />

              <notification-images v-if="hasClassicCampaignDisabledAndNotificationMmsEnabled"
                :custom-notification-images="customNotificationImages"
                :selected-notification-image="selectedNotificationImage"
                :suggested-notification-images="suggestedNotificationImages"
                @updateSelectedNotificationImage="updateSelectedNotificationImage"
              />

              <div class="data-description">
                <b>credit summary</b>

                <div>
                  characters {{ charactersForSms }}, credits {{ creditsForSms }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="pushTabSelected" class="push-container">
            <div class='campaign-content-toggle text-can-error' @click="pushExpanded = !pushExpanded"
                 v-has-error:push_send>
              <span>{{ pushExpanded ? '-' : '+'}}</span>
              native app message
            </div>

            <div v-if="pushExpanded">
              <!-- Michael asked to comment this one until native app supports multiple media by default, then we can expand its functionality to include media
              <div class='checkbox-container' @click="updateUseSameContent()">
                <sb-checkbox :checked="useSameContent"/>
                <p class='checkbox-text'> use same message as the stashboard message</p>
              </div> -->

              <push-campaign-creator
                ownerType="Campaign"
                :campaign="campaign"
                :ad="selectedAd"
                :pushContentEditor="pushContentEditor"
                @update:push-content-editor="updatePushContentEditorValue"
                @update:ad="updateAd"
                class="mb-10"
                @update:campaign="updateCampaign"
              />

              <push-campaign-notification-creator
                :campaign="campaign"
                :pushNotificationEditor="pushNotificationEditor"
                :charactersForPushNotificationContent="charactersForPushNotificationContent"
                @update:push-notification-editor="updatePushNotificationEditorValue"
                class="mb-10"
              />

              <div class="data-description">
                <b>credit summary</b>

                <div>
                  characters {{ charactersForPush }}, credits {{ pushCreditsDisplayNumber }}
                  <popper
                    trigger="clickToOpen"
                    :visible-arrow="false"
                    :options="{
                      placement: 'right',
                      modifiers: { offset: {offset: '0,10px'} }
                    }"
                  >
                    <div class="push-credits-popper popper shadow-none rounded-md">
                      with a push credit rate of {{ currentMerchant.push_value_percent }}% <br />
                      {{ creditsForPush }} &times; {{ currentMerchant.push_value_percent / 100 }} = {{ pushCreditsDisplayNumber }} credits
                      <div class="popper__arrow" x-arrow="" style="top: 13px;"></div>
                    </div>
                    <button slot="reference">
                      <sup><fai :icon="['fas', 'circle-question']" class="credit-info-icon"/></sup>
                    </button>
                  </popper>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedTab && selectedTab.key === 'email'" class="email-container">
            <div class='campaign-content-toggle text-can-error' @click="emailExpanded = !emailExpanded" v-has-error:email_send>
              <span v-if="emailExpanded">-</span>
              <span v-if="!emailExpanded">+</span>
              email content
            </div>

            <campaign-email
              v-if="emailExpanded"
              :campaign="campaign"
              :email="email"
              :emailSend="emailExpanded"
              @updateEmail="updateCampaignAndRefetchExtension"
              @cancelModalWithoutChanges="emailExpanded = false"
              @toggleEmailSend="emailExpanded = !emailExpanded"
              style="margin-bottom: 40px"
            />
          </div>

          <campaign-extensions
            v-if="!classicCampaignsEnabled"
            :campaign="campaign"
            :refetch-extension.sync="refetchExtension"
            @update:campaign="updateCampaign"
            @updateStashboardPreviewExtension="updateStashboardPreviewExtension"
          />

          <scheduler
            :range="schedulerRange"
            :selected-mode="scheduleMode"
            :selected-time.sync="scheduledTime"
            @update:selected-mode="updatedSchedulerMode"
            @update:selected-time="updateTime"
            v-has-error:send_at_local
          />

          <div
            class="flex flex-row items-stretch mt-10"
            v-if="(+campaign.sms_send +campaign.push_send +campaign.email_send) > 1"
          >
            <select-dropdown
              :selected.sync="campaign.send_method"
              :values="[
                { label: 'send all methods', value: 'all' },
                { label: 'send one method', value: 'desc' }
              ]"
              variant="contained"
              class="sb-4 mt-0 send-method-dropdown w-44"
              hideSelectedItem
              :truncateLabel="false"
            />
            <transition-group
              tag="div"
              class="flex pl-2 rounded-r-2xl -ml-3 border-solid"
              name="send-priority"
              v-if="campaign.send_method == 'desc'"
            >
              <div
                v-for="(send_method, index) in campaign.send_priorities" :key="send_method"
                class="flex-1 p-5 text-center text-xl"
              >
                <div class="text-xl">{{send_method == 'push' ? 'app' : send_method}}</div>
                <div class="text-3xl">
                  <span>
                    <button
                      :disabled="index === 0"
                      @click="toLeftSendMethod(send_method)"
                      class="send-priority-button"
                    >
                      <fai :icon="['fas', 'chevron-left']"/>
                    </button>
                  </span>
                  <span>{{index + 1}}</span>
                  <span>
                    <button
                      :disabled="index === campaign.send_priorities.length - 1"
                      @click="toRightSendMethod(send_method)"
                      class="send-priority-button"
                    >
                      <fai :icon="['fas', 'chevron-right']"/>
                    </button>
                  </span>
                </div>
              </div>
            </transition-group>
          </div>

        </div>

        <div class="send-campaign mt-0 sm:mt-14">
          <button class="larger submit button" @click="sendCampaign" data-cy="send-campaign-button">send campaign</button>
        </div>

        <ads-list
          v-if="!classicCampaignsEnabled"
          class="right-side"
          :adAsLink="adAsLink"
          :attachedAdId="adId"
          :campaignId="campaign.id"
          :campaignSendTime="campaign.send_at_local"
          :recipientCount="totalRecipientCount"
          :isEmailType="selectedTab && selectedTab.key === 'email'"
          :sendAtLocal="sendAtLocal"
          :sendNow="campaign.sendNow"
          :shouldShowAgreementModal="shouldShowAgreementModal"
          :smsSend="campaign.sms_send"
          :smsSent="campaign.sms_sent"
          @selectedAd="didSelectAd"
          @deselectedAd="didDeselectAd"
          ref="ads-list"
        />
      </section>

      <div class="spacer" :style="{ height: collapsibleDisplayExpanded ? '120px' : '0' }" />

      <autosized-collapsible-footer
        :expanded.sync="collapsibleDisplayExpanded"
        :class="{editing: isEditingKpis}"
      >
        <div class="header">
          <div class="title mr-1 sm:mr-6">
            <span class="hidden md:inline-block">selected recipients shopping habits on certain days</span>  <a @click="setIsEditingKpis(!isEditingKpis)">{{ isEditingKpis ? "finish" : "edit" }}</a>
          </div>
          <a
            v-if="isEditingKpis"
            @click="setIsCreatingKpi(!isCreatingKpi)"
          >
            {{ isCreatingKpi ? '-' : '+' }} add kpi
          </a>
          <day-of-week-picker
            v-else-if="this.caddieOptions.selected_kpis.length"
            :selectedDay="caddieSelectedDay"
            @selectDay="updateSelectedDay"
            @selectAllDays="clearSelectedDay"
          />
        </div>

        <div class="kpis-container">
          <span v-if="isKpiContainerEmpty" class="no-kpis-message" style="font-size: 1.5em">no kpis added</span>
          <span v-else-if="isCaddieLoading" class="no-kpis-message"><loading-icon /></span>
          <div v-if="isCreatingKpi" class="kpi-item">
            <campaign-kpi-box class="add-kpi">
              <template slot="label">add kpi</template>
              <template slot="content">
                <span v-if="!unusedKpis.length" class="no-kpis-message">no kpis to add</span>
                <div v-for="{label, key} in unusedKpis" @click="addKpi(key)" :key="key">
                  {{label}}
                </div>
              </template>
            </campaign-kpi-box>
          </div>
          <div v-for="kpiKey in caddieOptions.selected_kpis" :key="kpiKey" class="kpi-item" >
            <campaign-kpi-box-info
              :kpi="kpisData[kpiKey]"
              :isEditing="isEditingKpis"
              @removeKpi="removeKpi"
            />
          </div>
        </div>

      </autosized-collapsible-footer>

    </main>

  </div>
</template>

<script>
import AdsList from '../../components/AdsList/AdsList'
import NotificationImages from '../../components/NotificationImages/notification-images.vue';
import AgreementModal from "../../components/Campaigns/agreement-modal.vue";
import {mapGetters, mapState} from "vuex";
import {classicCampaignAPI} from "../../services/api/classicCampaignAPI";
import {campaignAPI} from "../../services/api/campaignAPI";
import {currentMerchantAPI} from "../../services/api/currentMerchant.js";
import {format, parseISO} from "date-fns";
import {textMarketingAPI} from "../../services/api/textMarketing"
import {hasEmoji, isWithinTime, nextAvailableHour, removeTimeZoneAndGetDateUtil} from "../../utilities";
import TextNotificationPicker from "../../components/TextNotificationPicker";
import { Counter, CollapsibleFixedFooter, ErrorBanner, HasError, LoadingIcon, TabBar, Touch } from "@springbig/sb-components";
import Scheduler from "./../../components/Campaigns/scheduler";
import CampaignCreator from "../../components/Campaigns/campaign-creator";
import PushCampaignCreator from "../../components/Campaigns/push-campaign-creator";
import PushCampaignNotificationCreator from "../../components/Campaigns/push-campaign-notification-creator";
import CampaignEmail from "../../components/Campaigns/CampaignEmail";
import RecipientDropdown from '../../components/Campaigns/recipient-dropdown.vue';
import CampaignExtensions from "../../components/Campaigns/extensions/campaign-extensions";
import {campaignEditValidators} from "./campaignEditValidators";
import {
  chain,
  debounce,
  omit,
  without
} from "lodash";
import {utcToZonedTime} from "date-fns-tz";
import Popper from 'vue-popperjs';
import Modal from '../../components/shared/Modal'
import DayOfWeekPicker from "./DayOfWeekPicker";
import CampaignKpiBox from "./CampaignKpiBox";
import CampaignKpiBoxInfo from "./CampaignKpiBoxInfo";
import StashboardPreview from "../../components/Campaigns/stashboard-preview";
import { stashboardAPI } from "../../services/api/stashboardAPI";
import asyncPool from "tiny-async-pool";
import AutosizedCollapsibleFooter from "../../components/shared/AutosizedCollapsibleFooter";
import { SelectDropdown } from "@springbig/sb-components";
import { notificationMessagesAPI } from "../../services/api/notificationMessagesAPI";

const defaultSchedulerRange = {
  date: {
    min: new Date(),
    max: null
  },
  time: {
    min: null,
    max: null
  }
},
defaultStashboardPreview = {
  content: null,
  extensionType: null,
  headers: null,
  image: null,
  merchantName: null,
  merchantWebsiteUrl: null,
}

export default {
  name: "campaign-edit",

  directives: {
    Touch, HasError
  },

  components: {
    CampaignKpiBox,
    CampaignKpiBoxInfo,
    AgreementModal,
    AdsList,
    CampaignCreator,
    PushCampaignCreator,
    PushCampaignNotificationCreator,
    CampaignEmail,
    CampaignExtensions,
    Counter,
    ErrorBanner,
    LoadingIcon,
    NotificationImages,
    Popper,
    RecipientDropdown,
    Scheduler,
    TabBar,
    TextNotificationPicker,
    Modal,
    DayOfWeekPicker,
    CollapsibleFixedFooter,
    StashboardPreview,
    AutosizedCollapsibleFooter,
    SelectDropdown,
  },

  data() {
    return {
      adFilters: null,
      attachmentUUID: null,
      caddieOptions: {
        available_kpis: [],
        selected_kpis: []
      },
      caddieSelectedDay: null,
      campaign: null,
      charactersForPush: 0,
      charactersForPushNotificationContent: 0,
      charactersForSms: 0,
      collapsibleDisplayExpanded: true,
      creditsForPush: 0,
      creditsForSms: 0,
      customNotificationImages: null,
      editorValue: '',
      email: {
        subject: null
      },
      emailExpanded: false,
      emailExtensionWarning: {
        showModal: false,
        text: '',
        title: '',
        ignore: false
      },
      emailTemplateExists: false,
      pushExpanded: false,
      pushSend: false,
      useSameContent: false,
      groupsWithSelected: null,
      isCaddieLoading: true,
      isCreatingKpi: false,
      isEditingKpis: false,
      isKpisRequestPending: false,
      isPostSaveCampaign: false,
      kpisData: {},
      loadingTotalCount: false,
      prevRecipientCount: 0,
      totalRecipientCount: 0,
      scheduledTime: null,
      scheduleMode: null,
      schedulerRange: defaultSchedulerRange,
      selectedAd: null,
      selectedGroups: [],
      selectedNotificationImage: null,
      selectedNotificationTemplate: null,
      selectedTab: null,
      sendingCampaign: false,
      shouldShowAgreementModal: false,
      showAdChangedModal: false,
      showAlertModal: false,
      smsExpanded: true,
      stashboardHeaders: null,
      stashboardPreview: defaultStashboardPreview,
      previewExtension: null,
      stashboardPreviewExpanded: false,
      suggestedNotificationImages: null,
      sendingMethod: null,
      templates: [],
      smsContentHasEmoji: false,
      pushContentEditor: '',
      pushNotificationEditor: '',
      refetchExtension: false,
      isLoadingNotifTemplates: false
    }
  },

  async created() {

    this.showAgreementModal();


    await this.loadMerchantData();
    await this.loadCampaignData();

    this.stashboardHeaders = await stashboardAPI.getStashboardHeaders();

    this.caddieOptions = this.currentMerchant.caddie_options
    this.isCaddieLoading = false;
    await this.updateSelectedDay( this.merchantTime.getDay() );

    this.$store.dispatch('CONFIGURE_VALIDATION', {
      validators: campaignEditValidators,
      values: {
        classicCampaignsEnabled: this.$store.getters.classicCampaignsEnabled,
        ...omit(this.campaign, ['send_at_local']),
        send_at_local: this.campaign.send_at_local ? parseISO(this.campaign.send_at_local) : null, //send_at_local needs to be a date object for validator
      }
    });

    /**
     * Prime validation by "interacting" with some elements where we haven't been able to implement "Touch" logic correctly
     */
    this.$store.dispatch('TOUCH_FIELDS', ['sms_send', 'push_send', 'email_send', 'send_at_local', 'send_now']);

    /**
     * Validate and generate any existing errors
     * Errors won't be displayed until user touches those fields
     */
    this.$store.dispatch('VALIDATE_ALL_FIELDS');

    this.setNotificationImagesForCurrentMerchant();

    this.stashboardPreview = {
      content: this.campaign.sms_content,
      extensionType: this.campaign.extension_type,
      headers: this.stashboardHeaders,
      merchantName: this.currentMerchant.display_name,
      merchantWebsiteUrl: this.currentMerchant.website
    }
  },

  methods: {
    moveSendMethod(send_method, idxOp) {
      let sps = this.campaign.send_priorities
      const idx = sps.indexOf(send_method)
      sps = without(sps, send_method)
      sps.splice(idxOp(idx), 0, send_method)
      this.campaign.send_priorities = sps
      this.updateCampaign({send_priorities: sps})
    },
    toLeftSendMethod(send_method) {
      this.moveSendMethod(send_method, (idx) => idx - 1)
    },
    toRightSendMethod(send_method) {
      this.moveSendMethod(send_method, (idx) => idx + 1)
    },
    updateSelectedGroups(selectedGroups) {
      this.selectedGroups = selectedGroups
    },
    loadMerchantData() {
      return this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    },
    async loadCampaignData() {
      this.isLoadingNotifTemplates = true;
      this.fetchNotifications().finally(()=>{
        this.isLoadingNotifTemplates = false;
      });
      const { campaign, template } = await this.currentCampaignAPI.fetch(this.campaignId);
      const groupsWithSelected = await campaignAPI.selectedGroups(campaign.id);

      if(groupsWithSelected) this.groupsWithSelected = groupsWithSelected;

      this.campaign = campaign;
      this.creditsForPush = campaign.credits_for_push;
      this.creditsForSms = campaign.credits;
      this.charactersForPush = campaign.characters_for_push;
      this.charactersForSms = campaign.characters;
      this.smsExpanded = campaign.sms_send;
      this.emailExpanded = campaign.email_send;
      this.editorValue = campaign.sms_content || '';
      this.sendingMethod = campaign.sending_method;
      this.pushContentEditor = campaign.push_content || '';
      this.pushNotificationEditor = campaign.push_notification_content || '';
      this.useSameContent = campaign.use_same_content;
      this.pushExpanded = campaign.push_send;
      this.emailTemplateExists = campaign.has_email_template;
      this.selectedTab = this.tabs[0];

      this.$store.dispatch('INITIALIZE_STASHBOARD_MEDIA', {
        owner: campaign,
        ownerType: 'Campaign',
        ownerId: campaign.id,
        stashboardCommunicationType: this.selectedTab.communicationType
      })

      if (campaign.ad) this.setSelectedAd(campaign.ad);

      if (campaign.send_at_local) {
        this.scheduledTime = new Date(campaign.send_at_local);
      }

      if(campaign.email_subject) {
        this.email.subject = campaign.email_subject;
      }

      if(template) {
        this.selectedNotificationTemplate = template;
      }

      if (campaign.template_image) {
        this.selectedNotificationImage = campaign.template_image;
      }

      this.smsContentHasEmoji = hasEmoji(campaign.sms_content);

      this.scheduleMode = campaign.send_now ? 'now' : 'later';

      this.refreshTotalCount();
      this.setNotificationImagesForCurrentMerchant();
    },
    setSelectedAd(ad) {
      this.selectedAd = ad;

      const params = {
        ad_id: ad.id,
        image_type: 'mms'
      };

      this.schedulerRange = {
        date: {
          min: ad.send_at_start ? new Date(ad.send_at_start) : null,
          max: ad.send_at_end ? new Date(ad.send_at_end) : null
        },
        time: {
          min: ad.time_range_start ? removeTimeZoneAndGetDateUtil(ad.time_range_start) : null,
          max: ad.time_range_end ? removeTimeZoneAndGetDateUtil(ad.time_range_end) : null
        }
      };

      if (this.schedulerRange.time.min !== null && !isWithinTime(this.schedulerRange.time.min, this.schedulerRange.time.max, this.merchantTime)) {
        this.scheduleMode = 'later';
        this.scheduledTime = nextAvailableHour(this.schedulerRange.time, this.merchantTime);

        params.send_at_local = this.scheduledTime;
        params.send_now = false;
      }

      // Shouldn't apply ad's filters if ad targets more than 1 retailer
      if (ad.retailers.length === 1 && (ad.merchant_tag_ids?.length || ad.merchant_segment_ids?.length)) {
        this.adFilters = {
          segments: ad.merchant_segment_ids,
          tags: ad.selected_groups.find(({id}) => id === 'category')?.display_names?.join(',') || '',
          groups_with_selected: ad.selected_groups
        };
      }
      else {
        this.adFilters = null;
      }

      this.$store.dispatch('UPDATE_VALUES', {
        schedulerRange: this.schedulerRange,
        send_at_local: this.scheduledTime
      });

      return params;
    },
    didSelectAd(ad) {
      if (!ad) {
        this.adFilters = null
        return
      }

      const params = this.setSelectedAd(ad)

      if (ad.suggested_text){
        this.updateEditorAndStashboardPreviewContentValue(ad.suggested_text);
        this.updatePushContentEditorValue(ad.suggested_text);
      }

      if(Object.keys(params).length > 0) {
        this.updateCampaign(params);
      }

      this.$store.dispatch('ADD_AD_STASHBOARD_MEDIA', {ad: ad})
    },

    didDeselectAd(ad) {
      this.scheduleMode = 'now';
      this.schedulerRange = defaultSchedulerRange;
      this.scheduledTime = null;
      const params = {
        send_now: true
      }

      if(this.selectedAd) {
        params.ad_id = null;
      }

      if (!!this.adFilters) { this.adFilters = null }

      this.selectedAd = null;

      this.$store.dispatch('UPDATE_VALUES', {
        schedulerRange: this.schedulerRange,
        send_at_local: this.scheduledTime
      });

      if (ad?.suggested_text) {
        this.updateEditorAndStashboardPreviewContentValue('');
        this.updatePushContentEditorValue('');
      }
      this.updateCampaign(params);
    },

    async refreshTotalCount() {
      this.loadingTotalCount = true;
      this.totalRecipientCount = await campaignAPI.totalRecipientCount(this.campaign.id, this.selectedTab.key);
      this.prevRecipientCount = this.totalRecipientCount;
      this.loadingTotalCount = false;
    },

    updateEditorAndStashboardPreviewContentValue(newVal) {
      this.editorValue = newVal;
      this.stashboardPreview.content = newVal;
      if (this.useSameContent) {
        this.pushContentEditor = newVal;
        this.updateContents()
      } else {
        this.updateCampaign({sms_content: newVal});
      }
    },

    updatePushContentEditorValue(newVal) {
      this.pushContentEditor = newVal;
      if (this.useSameContent) {
        this.editorValue = newVal;
        this.updateContents()
      } else {
        this.updateCampaign({push_content: newVal});
      }
    },

    updateContents() {
      this.updateCampaign({
        sms_content: this.editorValue,
        push_content: this.pushContentEditor
      });
    },

    updatePushNotificationEditorValue(newVal) {
      this.pushNotificationEditor = newVal;
      this.updateCampaign({
        push_notification_content: newVal
      });
    },

    hasPushSendingMethodSelected() {
      const pushSendingMethods = [
        'sms_and_push_notification',
        'push_notification_only',
        'push_notification_or_sms'
      ]

      return pushSendingMethods.includes(this.sendingMethod)
    },

    updateUseSameContent() {
      this.useSameContent = !this.useSameContent;
    },

    updateStashboardPreviewExtension(event) {
      this.previewExtension = event
      this.stashboardPreview.extensionType = event?.type
    },

    updateAd(newVal, oldAd) {
      //Editor can only remove ads atm
      const hasSuggestedText = !!oldAd.suggested_text;
      this.$refs['ads-list'].removeAd(hasSuggestedText, oldAd);
      this.updateEditorAndStashboardPreviewContentValue(hasSuggestedText ? oldAd.suggested_text : "");

      this.$store.dispatch('UPDATE_VALUES', {
        schedulerRange: null
      });
    },

    updateSelectedTab(newTab) {
      this.selectedTab = newTab;

      if(this.selectedTab.key === "push") {
        this.openPushContent()
      }
      this.$store.commit('SET_STASHBOARD_COMMUNICATION_TYPE', newTab.communicationType)
      this.refreshTotalCount();
    },

    openPushContent() {
      this.pushExpanded = true
    },

    async sendCampaign() {
      // Reset event so ad, brand template, customer filters don't get wiped out
      window.onbeforeunload = null

      this.$store.dispatch('TOUCH_ALL_FIELDS');

      if(this.$store.getters.hasSomeError) {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
      } else {
        if(this.selectedAd) {
          const data = await this.$refs['ads-list'].getAds();
          const newAd = data?.find(ad => ad.id === this.selectedAd.id)
          if(newAd.attachments[0].uuid !== this.selectedAd.attachments[0].uuid) {
            this.updateAd(null, this.selectedAd);
            this.showAdChangedModal = true;
            return;
          }
        }

        // check if either campaign has extension and email does not have link placed
        // or if email has link and campaign has no extension
        if(this.emailExpanded && !this.emailExtensionWarning.ignore) {
          // Track offer or target wallet link (anylink is not included)
          const campaignHasExtension = [1, 3, 5, 6].includes(parseInt(this.campaign.extension_type));
          const emailHasExtensionLink = this.campaign.email_contains_extension;

          if (emailHasExtensionLink && !campaignHasExtension) {
            this.emailExtensionWarning = {
              showModal: true,
              title: "<b>warning! campaign extension is not </br>configured<b>",
              text: "we have detected the \"extension link\" content in your email, however an extension has not yet been configured in your camapign. please edit and add an extension to your campaign or an extension will not send to your members",
              ignore: false
            };
            return
          } else if (!emailHasExtensionLink && campaignHasExtension) {
            this.emailExtensionWarning = {
              showModal: true,
              title: "<b>warning! campaign extension is not </br>configured in your email</b>",
              text: "we have detected that an extension link is attached to your campaign but has not been added to your email content. please edit your email and add the \"extension link\" content to the email or it will not send to your members",
              ignore: false
            };
            return
          }
        }

        this.sendingCampaign = true;
        await campaignAPI.sendCampaign(this.campaign.id);
        window.location = `/campaigns/${this.campaign.id}`;
      }
    },

    async setNotificationImagesForCurrentMerchant() {
      if(this.hasClassicCampaignDisabledAndNotificationMmsEnabled) {
        const { shared_images, merchant_images } = await currentMerchantAPI.getNotificationImages("campaign")
        this.customNotificationImages = merchant_images
        this.suggestedNotificationImages = shared_images
      }
    },

    async rejectAgreementModal(agreementCheck){
      try {
        await textMarketingAPI.PATCHTextMarketing({ checked_dont_show_text_marketing_warning: agreementCheck });
      } catch(error) {
        // TODO: Handle Error
        console.log(error);
      }
      this.shouldShowAgreementModal = false;
    },

    async showAgreementModal() {
      try{
        const data = await textMarketingAPI.GETTextMarketing();
        this.shouldShowAgreementModal = data.should_show_modal;
      } catch(error){
         console.log(error)
      }
    },

    async updatedSelectedNotification(newTemplate) {
      await this.updateCampaign({ template_id: newTemplate.id });
      this.fetchNotifications();
    },

    updateSelectedNotificationImage(image) {
      this.updateCampaign({ template_image_id: (image) ? image.id : null });
    },

    updatedSchedulerMode(mode) {
      this.scheduleMode = mode;
      if(mode === 'now') {
        this.updateCampaign({ send_now: true });
      } else {
        //We have to do this manually because updating the api to send_now:false requires a selected time
        this.$store.dispatch('UPDATE_VALUES', { send_now: false });
        this.$store.dispatch('VALIDATE', ['send_at_local'])

        if(this.scheduledTime) {
          this.updateCampaign({ send_at_local: this.scheduledTime, send_now: false });
        }
      }
    },

    async updateSelectedRecipients(frag) {
      await campaignAPI.updateGroups(this.campaign.id, frag);
      await this.refreshTotalCount();
    },

    updateTagSubfilter: debounce(async function (frag) {
      await campaignAPI.updateTagSubfilter(this.campaign.id, frag);
      await this.refreshTotalCount();
    }, 1000),

    updateTime(newDate) {
      this.$store.dispatch('UPDATE_VALUES', { send_at_local: newDate });
      this.updateCampaign({ send_at_local: newDate, send_now: this.scheduleMode === 'now' })
        this.updateSelectedDay(newDate.getDay())
    },

    async updateCampaignAndRefetchExtension(frag) {
      await this.updateCampaign(frag);
      this.refetchExtension = true;
    },

    async updateCampaign(frag) {
      const allFields = Object.keys(frag);

      await this.$store.dispatch('UPDATE_VALUES', omit(frag, ['send_at_local']));
      await this.$store.dispatch('VALIDATE', allFields);

      /**
       * KLUDGE: We didn't refactor some of the nested components
       * This supports old workflow component stuff
       */
      if(allFields.includes('sms_content')) {
        this.smsContentHasEmoji = hasEmoji(frag['sms_content']);
      }

      if(allFields.includes('email_subject')) {
        this.email.subject = frag['email_subject'];
      }

      if(allFields.includes('has_email_template')) {
        this.emailTemplateExists = frag['has_email_template'];
      }

      if(frag.send_at_local) {
        frag.send_at_local = format(frag.send_at_local,'yyyy-MM-dd HH:mm');
      }

      const { campaign, characters, credits, characters_for_push, credits_for_push, characters_for_push_notification_content } = await this.currentCampaignAPI.patch(this.campaign.id, frag);
      this.selectedNotificationImage = campaign.template_image;
      this.campaign = campaign;
      this.creditsForSms = credits;
      this.charactersForSms = characters;
      this.charactersForPush = characters_for_push;
      this.creditsForPush = credits_for_push;
      this.charactersForPushNotificationContent = characters_for_push_notification_content
    },

    updateSelectedDay(dayNumber) {
      this.caddieSelectedDay = dayNumber;
    },

    clearSelectedDay() {
      this.caddieSelectedDay = null;
    },

    setIsEditingKpis( isEditing ) {
      this.isEditingKpis = isEditing
      if(!isEditing) this.setIsCreatingKpi(false);
    },

    setIsCreatingKpi( isCreating ) {
      this.isCreatingKpi = isCreating
      if(isCreating) document.getElementsByClassName('kpis-container')[0].scrollLeft = 0
    },

    async addKpi(kpiKey) {
      const selected_kpis = [kpiKey, ...this.caddieOptions.selected_kpis];
      const options = { selected_kpis };
      this.caddieOptions.selected_kpis = selected_kpis
      this.setIsCreatingKpi(false);
      const { data: optionsData } = await currentMerchantAPI.PATCHCaddieOptions({ caddie_options: options });
      this.caddieOptions.selected_kpis = optionsData.caddie_options.selected_kpis;
      const { data: kpisData } = await campaignAPI.getCampaignKpis(this.campaignId, kpiKey, this.caddieSelectedDay, this.selectedTab.key)
      this.addItemToKpisData(kpisData);
    },

    async removeKpi(kpiKey) {
      const options = { selected_kpis: this.caddieOptions.selected_kpis.filter( key => key!==kpiKey ) };
      const { data } = await currentMerchantAPI.PATCHCaddieOptions({ caddie_options: options });
      this.caddieOptions = data.caddie_options;
    },

    async updateKpis() {
      if(this.isKpisRequestPending || !this.caddieOptions || !this.collapsibleDisplayExpanded) return;

      this.kpisData = {}
      this.isKpisRequestPending = true
      const currentRequestDay = this.caddieSelectedDay;
      const currentRequestGroups = this.selectedGroups;
      const currentSelectedTabKey = this.selectedTab.key;

      const promisesArgs = [];
      for(let kpiKey of this.caddieOptions.selected_kpis) {
        promisesArgs.push([this.campaignId, kpiKey, this.caddieSelectedDay, this.selectedTab.key])
      }

      const concurrencyLimit = 4;

      const getCampaignKpis = (args) => campaignAPI.getCampaignKpis(...args)
      for await (const result of asyncPool(concurrencyLimit, promisesArgs, getCampaignKpis)) {
        this.addItemToKpisData(result.data);
        if( this.shouldUpdateKpis(currentRequestDay, currentRequestGroups, currentSelectedTabKey) ) break;
      }
      this.isKpisRequestPending = false;
      if( this.shouldUpdateKpis(currentRequestDay, currentRequestGroups, currentSelectedTabKey) ) {
        this.updateKpis();
      }
    },

    shouldUpdateKpis(requestDay, requestGroups, selectedTabKey) {
      return this.caddieSelectedDay !== requestDay || this.selectedGroups !== requestGroups || this.selectedTab.key !== selectedTabKey;
    },

    addItemToKpisData(data) {
      this.kpisData = {
        ...this.kpisData,
        [data.key]: {
          ...data,
          results: data.results.map(k => ({
            ...k,
            percent: k.total_count ? Math.round((k.count / k.total_count) * 100) : 0
          }))
        }
      }
    },

    syncSendPriorities() {
      const campaign = this.campaign
      const vm = this
      const send_method = campaign.send_method;
      const default_priorities = ["push", "sms", "email"]
      const priorities = send_method == 'all' ? null : chain(default_priorities)
        .unshift(...(campaign.send_priorities || []))
        .filter((comm_type) => vm[comm_type + "Expanded"])
        .compact()
        .uniq()
        .value()
      campaign.send_priorities = priorities
      return {
        send_method: send_method,
        send_priorities: priorities
      }
    },

    async fetchNotifications() {
      this.templates = await notificationMessagesAPI.fetchNotifications({ 'campaign_id': this.campaignId });
    }
  },

  watch: {
    smsExpanded() {
      const priorities = this.syncSendPriorities();
      this.updateCampaign({ sms_send: this.smsExpanded, ...priorities });
    },
    emailExpanded() {
      const priorities = this.syncSendPriorities();
      this.updateCampaign({ email_send: this.emailExpanded, ...priorities });
    },
    pushExpanded() {
      const priorities = this.syncSendPriorities();
      this.updateCampaign({ push_send: this.pushExpanded, ...priorities });
    },
    useSameContent() {
      if (this.useSameContent) {
        this.pushContentEditor = this.editorValue;
        this.updateEditorAndStashboardPreviewContentValue(this.pushContentEditor);
        this.updatePushContentEditorValue(this.pushContentEditor);
      }
      this.updateCampaign({use_same_content: this.useSameContent});
    },
    caddieSelectedDay() {
      this.updateKpis();
    },
    collapsibleDisplayExpanded(value) {
      if(value) this.updateKpis();
    },
    selectedTab() {
      this.updateKpis();
    },
    selectedGroups() {
      this.updateKpis();
    },
    '$store.state.stashboardMedia.ownerStashboardMedia': {
      immediate: false,
      deep: true,
      async handler() {
        if(this.campaign?.id) {
          const resp = await this.currentCampaignAPI.getCredits(this.campaign.id)
          this.creditsForSms = resp.credits_for_sms
          this.charactersForSms = resp.characters_for_sms
          this.creditsForPush = resp.credits_for_push
          this.charactersForPush = resp.characters_for_push
        }
      }
    },
    'campaign.send_method': function(send_method, old_method) {
      if (send_method == old_method) return;

      const priorities = this.syncSendPriorities();
      this.updateCampaign(priorities);
    },
  },

  computed: {
    currentCampaignAPI() {
      return this.classicCampaignsEnabled ? classicCampaignAPI : campaignAPI;
    },

    tabs() {
      let tabs = []
      const prefixStashboardTab = this.smsExpanded ? '' : '+ add',
            prefixPushTab = this.pushExpanded ? '' : '+ add',
            prefixEmailTab = this.emailExpanded ? '' : '+ add',
            stashboardLabel = "text";
      tabs.push(
        {label: `${prefixStashboardTab} ${stashboardLabel} message`, key: 'stashboard', communicationType: 'mms'},
        {label: `${prefixPushTab} native app message`, key: 'push', communicationType: 'push'},
        {label: `${prefixEmailTab} email`, key: 'email', communicationType: 'email'}
      )
      if (!this.hasPushSendingMethodSelected()) {
        tabs.splice(1, 1)  // removing native app tab
      }

      return tabs
    },
    adAsLink() {
      /**
       * TODO: Determine if ad is added as a link
       */
      return false
    },
    adId() {
      return this.selectedAd?.id
    },

    campaignId() {
      return this.$route.params.id
    },

    hasClassicCampaignDisabledAndNotificationMmsEnabled(){
      return !this.classicCampaignsEnabled && this.hasNotificationMmsMetaEnabled
    },

    pushTabSelected() {
      return this.selectedTab && this.selectedTab.key === 'push'
    },

    pushCreditsDisplayNumber() {
      var credits = this.creditsForPush * this.currentMerchant.push_value_percent / 100
      return credits = +credits.toFixed(2)
    },

    sendAtLocal() {
      if(!this.campaign) {
        return false;
      }

      return format(removeTimeZoneAndGetDateUtil(this.campaign.send_at_local) || new Date(), 'yyyy-MM-dd HH:mm')
    },

    merchantTime() {
      return utcToZonedTime(new Date(), this.currentMerchant.merchant_time_zone);
    },

    unusedKpis() {
      return this.caddieOptions.available_kpis.filter(({key}) => !this.caddieOptions.selected_kpis.includes(key));
    },

    isKpiContainerEmpty() {
      return !this.caddieOptions.selected_kpis.length
        && !this.isCreatingKpi
        && !this.isCaddieLoading;
    },

    ...mapGetters([
      'classicCampaignsEnabled',
      'hasNotificationMmsMetaEnabled',
      'currentMerchant',
      'newEmojiCalculationTypeEnabled',
      'ownerStashboardMedia'
    ]),
    ...mapState(['emojiAlertMessage', 'classicEmojiAlertMessage'])
  },
}
</script>

<style lang="scss" scoped>

.page-header {
  font-size: 24px;
}

.content {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr auto;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
}

.campaign-content-toggle,
.stashboard-preview-toggle {
  font-family: "AileronLight",sans-serif;
  font-size: 1.5rem;
  color: #128ff9;
  cursor: pointer;
  margin-bottom: 20px;
  &:hover {
    color: #016fb9;
  }
}

.reward-product-url-toggle {
  font-family: "AileronLight",sans-serif;
  font-size: 1rem;
  color: #128ff9;
  cursor: pointer;
  margin-bottom: 20px;
  &:hover {
    color: #016fb9;
  }
}

.send-campaign {
  grid-area: 2 / 1 / 3 / 2;
  text-align: center;
  width:100%;
}

button.submit {
  font-family: "AileronThin",sans-serif;
  margin:0 auto;
  width: 50%;
  border-radius: 16px;
}

.error-banner {
  margin-bottom: 1rem
}

.campaign-tabs::v-deep {
  overflow: unset;
  border-radius: 16px;
  border-width: 1px;
  margin-bottom: 15px;
  text-align: center;
  .sb-button {
    height: 100%;
    justify-content: center;
    align-items: center;
    font-family: "AileronThin",sans-serif;
    font-size: 1.125em;
    padding-bottom: 0.65em;
    padding-top: 0.65em;
    border-radius: 16px;
    margin: -1px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 1.7em;
    -webkit-line-clamp: 1; /* number of lines to show */
    line-clamp: 1;
    -webkit-box-orient: vertical;

    &.invert {
      margin: 0 1px;
    }
  }
}

.left-side {
  grid-area: 1 / 1 / 2 / 2;
  border-right: 1px solid #A6D4FC;
  padding-right: 44px;
}

.right-side {
  grid-area: 1 / 2 / 3 / 3;
}

.data-description {
  padding-bottom: 30px;
  border-bottom: 1px solid #128ff9;
}

.notification-picker::v-deep {
  .label-container {
    border-radius: 16px;
  }
  .items-container {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
}

.notification-picker.has-error::v-deep .label-container,
.scheduler.has-error::v-deep .label-container {
  border-color: rgba(249,18,83,.5);
}

.kpis-container {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  padding: 0 10px;
  min-height: 175px;
}

.collapsible-display::v-deep section.content-container {
  overflow: hidden;
}

.collapsible-display .header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 1.2rem;
  line-height: 1.8rem;
  padding: 5px 20px 0;
  white-space: nowrap;
}

.collapsible-display::v-deep .kpi-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
}

.collapsible-display.editing::v-deep .open-button {
  display: none;
}

.add-kpi::v-deep .kpi-content>div {
  text-align: left;
  color:#128ff9;
  cursor: pointer;
  line-height: 30px;
  user-select: none;
}
.add-kpi::v-deep .kpi-content {
  margin-right:-10px;
}

.collapsible-display::v-deep .no-kpis-message {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  align-self: center;
  color: #a4a4a4;
}

.banner {
  background-color: rgba(18,143,249,0.15);
  text-align: center;
  margin-bottom: 20px;
  border-radius: 8px;
  padding: 10px;
}

@media only screen and (max-width: 1023px) {
  .content {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr repeat(2, auto);
    grid-column-gap: 10px;
    grid-row-gap: 10px;
  }

  .content,
  .page-header {
    padding: 0;
  }

  .page-header {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .left-side {
    border: none;
    min-width: 100%;
    max-width: 715px;
    padding-right: 0;
    grid-area: auto;
    order: 1;
  }

  .right-side {
    grid-area: auto;
    order: 2;
  }

  .campaign-tabs {
    width: 100%;
  }

  .recipient-dropdown::v-deep main.label-container {
    padding: 1rem;
  }

  .recipient-dropdown::v-deep main.label-container,
  .campaign-tabs .sb-button,
  .recipient-dropdown::v-deep .preview {
    font-size: .92rem;
  }

  .campaign-content-toggle {
    font-size: 1.25rem;
  }

  .send-campaign {
    grid-area: auto;
    order: 3;
    z-index: 2;
    padding: 0 1rem;
  }

  button.submit {
    width: 100%;
  }

  .collapsible-display .header {
    font-size: 1rem;
    line-height: 1.2rem;
    padding: 5px 10px 0;
  }
}

.email-extension-warning-modal::v-deep .body {
  width: 500px;
}

.email-extension-warning-modal::v-deep .button-wrapper {
  display: flex;
  flex-direction: column;
}

.email-extension-warning-modal::v-deep .button {
  font-family: "AileronThin", sans-serif;
  padding: 15px 35px 15px 35px;
  margin: 10px auto 10px auto;
}

.email-extension-warning-modal::v-deep .proceed-link {
  font-size: large;
}

.push-credits-popper.popper {
  border: 1px solid #128ff9;
}

.credit-info-icon {
  color: #128ff9;
  cursor: pointer;
}

.popper .popper__arrow {
  border-color: transparent #128ff9 transparent transparent;
}

.checkbox-container {
  display: flex;
}

.checkbox-text {
  margin-left: 5px;
  line-height: 15px;
}

.spacer {
  transition: height 0.35s cubic-bezier(0.65, 0, 0.35, 1);
}

// send priorities
.send-method-dropdown::v-deep .sb-dropdown-label {
  font-family: 'AileronThin';
  font-size: 18px;
  text-align: center;
}

.send-method-dropdown::v-deep .sb-select-row {
  text-align: center;
  font-size: 18px;
  font-family: 'AileronLight';
}

.send-method-dropdown::v-deep .sb-rows-container {
  border-radius: 0 0 16px 16px;
}

.send-method-dropdown::v-deep {
  &.focused .sb-label-wrapper {
    border-radius: 16px 16px 0 0 !important;
  }
  .sb-label-wrapper {
    border-radius: 16px !important;
    padding: 1.5em !important;
    svg {
      font-size: 19.2px;
    }
  }
}
.send-priority-move, /* apply transition to moving elements */
.send-priority-enter-active,
.send-priority-leave-active {
  transition: all 0.5s ease;
}
.send-priority-enter-from,
.send-priority-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.send-priority-leave-active {
  position: absolute;
}
.send-priority-button {
  color: #148ff9;
  cursor: pointer;
}
.send-priority-button:disabled {
  color: #e1f0ff;
  cursor: not-allowed;
}
</style>
